import cn from 'classnames';
import { CleaveOptions } from 'cleave.js/options';
import Cleave from 'cleave.js/react';
import uniqueId from 'lodash-es/uniqueId';
import * as React from 'react';
import { useImperativeHandle, useRef, useState } from 'react';
import { BaseInputProps } from '@tcl-argon-models/models';
import { mergeClasses, StatusVariant } from '@tcl-argon-utils/utils';
import { useInputStyles } from './styles';
import './styles.scss';
import { useCommonInputStyles } from './useCommonInputStyles';

type HTMLInputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

// TODO I omitted the translate prop because of a new type error brought up by adding Refs.
// I want to see if omitting that prop 'fixes' the error, because the alternative is removing refs
export interface InputProps extends Omit<BaseInputProps, 'theme'>, Omit<HTMLInputProps, 'translate' | 'enterKeyHint'> {
  /** Force the input to appear focused. Useful for demos */
  forceFocusHighlight?: boolean;
  /** Insert an Icon into the input to the left of the text entry component */
  leftIcon?: React.ReactNode;
  /** An input mask, for controlling the auto-formatting of entered data. Uses Cleave.js, see their docs for more info. */
  mask?: CleaveOptions;
  /** Insert an Icon into the input to the right of the text entry component */
  rightIcon?: React.ReactNode;
  /** input status will affect colors that reflect that status. 'error' | 'warning' | 'success' | 'default' */
  status?: StatusVariant;
  /** style will change on input if isPrimary is false. If isPrimary is true, default styling will be applied */
  isPrimary?: boolean;
  /** object to handle clearing action on input field */
  clearable?: React.ReactNode;
}

export interface InputRefs {
  readonly input: HTMLInputElement;
  readonly label: HTMLLabelElement;
  readonly root: HTMLDivElement;
}

export const Input = React.forwardRef<InputRefs, InputProps>(
  (
    {
      className,
      classes: classOverrides,
      disabled = false,
      forceFocusHighlight = false,
      hideLabel = false,
      id,
      label,
      leftIcon,
      mask,
      onChange,
      rightIcon,
      subContent,
      status = 'default',
      isPrimary = true,
      clearable,
      value,
      ...inputProps
    },
    ref,
  ) => {
    [id] = useState(id || uniqueId('input_'));

    const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
    const labelRef = useRef<HTMLLabelElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      get input() {
        return inputRef.current as HTMLInputElement;
      },
      get label() {
        return labelRef.current as HTMLLabelElement;
      },
      get root() {
        return rootRef.current as HTMLInputElement;
      },
    }));

    React.useEffect(() => {
      if (
        forceFocusHighlight &&
        inputRef !== null &&
        inputRef !== undefined &&
        inputRef.current !== null &&
        inputRef.current !== undefined
      ) {
        inputRef.current.focus();
      }
    }, [forceFocusHighlight]);
    const clearInput = () => {
      if (inputRef !== null && inputRef !== undefined && inputRef.current !== null && inputRef.current !== undefined) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    };

    const classes = mergeClasses(
      useCommonInputStyles({ status, subContent }),
      useInputStyles({
        status,
        leftIcon,
        rightIcon,
        isPrimary,
        clearable: clearable !== null || clearable !== undefined,
        theme: {},
      }),
      classOverrides || {},
      { root: className || '' },
    );

    return (
      <div ref={rootRef} className={classes.root}>
        <label ref={labelRef} className={cn(classes.label, { [classes.isVisuallyHidden]: hideLabel })} htmlFor={id}>
          {label}
        </label>
        <div
          className={cn(classes.inputWrapper, {
            [classes.disabled]: disabled,
            [classes.forceFocus]: forceFocusHighlight,
          })}
        >
          {leftIcon && <div className={classes.leftIcon}>{leftIcon}</div>}
          {mask == null ? (
            <input
              ref={inputRef}
              className={classes.input}
              disabled={disabled}
              id={id}
              onChange={onChange}
              value={value}
              {...inputProps}
            />
          ) : (
            <Cleave
              htmlRef={(inputNode: HTMLInputElement) => {
                if (!inputRef) {
                  return;
                } else {
                  inputRef.current = inputNode;
                }
              }}
              className={classes.input}
              disabled={disabled}
              id={id}
              options={mask}
              onChange={onChange}
              value={value}
              // Cleave should accept all props that <input/> elements accept, but the typing is borked.
              // This assertion forces compliance.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(inputProps as any)}
            />
          )}
          <div className={classes.rightIconClearable}>
            {clearable && value !== '' && value !== null && value !== undefined && (
              <div className={cn(classes.clearable)} onClick={clearInput} onKeyPress={clearInput}>
                {clearable}
              </div>
            )}
            {clearable && rightIcon && value !== '' && value !== null && value !== undefined && (
              <div className={classes.divider}></div>
            )}
            {rightIcon && <div>{rightIcon}</div>}
          </div>
        </div>
        {subContent && <p className={classes.subContent}>{subContent}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';
